import React, { useState } from 'react';
import { User as UserIcon, Pencil, Check, X, Bookmark } from 'lucide-react';
import { useRobloky } from '../../context/RoblokyContext';
import { useAuth } from '../../context/AuthContext';

export const ProfileView: React.FC = () => {
  const { profile, updateProfile, openSaved, openAuthModal, savedGames } = useRobloky();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');

  if (!user || !profile) {
    return (
      <div className="text-center py-16 space-y-3 max-w-md mx-auto">
        <p className="text-slate-200 font-bold">Your gamer profile lives here</p>
        <p className="text-xs font-mono text-slate-500">
          Sign in to like games, write reviews and build your library.
        </p>
        <button
          onClick={() => openAuthModal('signin')}
          className="mt-2 px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-xs font-bold transition-all"
        >
          Sign In
        </button>
      </div>
    );
  }

  const startEdit = () => {
    setName(profile.displayName);
    setBio(profile.bio);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!name.trim()) return;
    await updateProfile({ displayName: name.trim().slice(0, 40), bio: bio.trim().slice(0, 300) });
    setEditing(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="rounded-2xl border border-white/10 bg-[#0d111a] p-5 flex items-center gap-4">
        {profile.avatar ? (
          <img src={profile.avatar} alt="" className="h-16 w-16 rounded-2xl object-cover border border-red-500/40" />
        ) : (
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/15 border border-red-500/40">
            <UserIcon className="h-7 w-7 text-red-300" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-white truncate">{profile.displayName}</h1>
            {profile.role === 'admin' && (
              <span className="rounded bg-red-500/15 border border-red-500/40 px-1.5 py-0.5 text-[10px] font-mono font-bold text-red-300">
                ADMIN
              </span>
            )}
          </div>
          <p className="text-[11px] font-mono text-red-400/80">{profile.tag}</p>
          {profile.bio && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{profile.bio}</p>}
        </div>
        {!editing && (
          <button
            onClick={startEdit}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-slate-300 text-xs font-bold hover:border-red-500/40 transition-all shrink-0"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>

      {editing && (
        <div className="rounded-2xl border border-red-500/30 bg-[#0d111a] p-5 space-y-3">
          <label className="block space-y-1">
            <span className="text-[11px] font-mono text-slate-400 uppercase">Display name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              className="w-full px-3.5 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm text-white focus:border-red-500 focus:outline-none"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-mono text-slate-400 uppercase">Bio</span>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={300}
              rows={3}
              className="w-full px-3.5 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm text-white focus:border-red-500 focus:outline-none resize-none"
            />
          </label>
          <div className="flex gap-2">
            <button
              onClick={saveEdit}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-500 hover:bg-red-400 text-white text-xs font-bold transition-all"
            >
              <Check className="h-3.5 w-3.5" /> Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 border border-white/10 text-slate-300 text-xs font-bold transition-all"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      <button
        onClick={openSaved}
        className="w-full flex items-center justify-between rounded-2xl border border-white/10 bg-[#0d111a] p-4 hover:border-red-500/40 transition-all"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-slate-200">
          <Bookmark className="h-4 w-4 text-red-400" />
          My Library
        </span>
        <span className="text-xs font-mono text-slate-400">{savedGames.length} saved</span>
      </button>
    </div>
  );
};
